// @flow
import { ResourceResponseContext } from 'components/ResourceFill/providers/ResourceResponseProvider'
import { ResourceSessionsContext } from 'components/ResourceFill/providers/ResourceSessionsProvider'
import { ResourceSharedContext } from 'components/ResourceFill/providers/ResourceSharedProvider'
import * as React from 'react'
import { arrayUpdateByKey } from 'utils/helpers'
import { ZentakeContext } from 'utils/ZentakeContext'

import type { DocumentContext } from '../types/document-context'

export const ResourceDocumentContext: React.Context<?DocumentContext> =
  React.createContext(null)

type Props = {
  children: React.Node,
}

function ResourceDocumentProvider({ children }: Props): React.Node {
  const {
    currentSession,
    setSessions,
    sessions,
    sessionId,
    setCurrentSession,
  } = React.useContext(ResourceSessionsContext) || {}

  const { formIndex } = React.useContext(ResourceSharedContext) || {}

  const { response } = React.useContext(ResourceResponseContext) || {}

  const { submitFunctions } = React.useContext(ZentakeContext)

  const saveDocument = React.useCallback(
    async (validate, submit = false, lockInternalForm = false) => {
      if (!currentSession) return
      if (!submitFunctions.document) return
      if (!setSessions) return
      if (!sessions) return
      if (!sessionId) return
      if (!setCurrentSession) return

      const result = await submitFunctions.document({
        validate,
        formIndex,
        submit,
        lockInternalForm,
      })

      if (!result) return null

      const updatedResponse = {
        ...response,
        ...result,
        document_response: {
          ...(response?.document_response || {}),
          ...result,
        },
      }

      const newCurrentSession = {
        ...currentSession,
        responses: arrayUpdateByKey(
          currentSession?.responses,
          'id',
          updatedResponse?.id,
          updatedResponse
        ),
      }

      setSessions({ ...sessions, [sessionId]: newCurrentSession })

      setCurrentSession(newCurrentSession)

      return newCurrentSession
    },
    [
      currentSession,
      formIndex,
      response,
      sessionId,
      sessions,
      setCurrentSession,
      setSessions,
      submitFunctions,
    ]
  )

  const value = React.useMemo(() => ({ saveDocument }), [saveDocument])

  return (
    <ResourceDocumentContext.Provider value={value}>
      {children}
    </ResourceDocumentContext.Provider>
  )
}

export default ResourceDocumentProvider
